<template>
    <div class="EntityDetailsCollateralSection text-eidDarkBlue">
        <template v-if="!localCollaterals || localCollaterals.length === 0">
            <div
                data-test-id="EntityDetailsCollateralSection__noCollateralsText"
                class="EntityDetailsCollateralSection__emptyContainer"
            >
                <template v-if="editingEnabled">
                    Click "Add Collateral" to add collateral(s). Once added, collateral can be evaluated for compliant
                    status. You can also set up decisioning criteria in risk profiles for each collateral category.
                </template>
                <template v-else>
                    Click "Edit Entity Details" to add collateral(s). Once added, collateral can be evaluated for compliant
                    status. You can also set up decisioning criteria in risk profiles for each collateral category.
                </template>
            </div>
        </template>
        <template v-else>
            <Table self-contained>
                <template #head>
                    <HeadCell
                        v-for="{ name, label, tooltipData } in columns"
                        :key="name"
                        :data-test-id="`EntityDetailsCollateralSection__${name}HeadCell`"
                        class="text-eidDarkBlue font-semibold"
                    >
                        <div v-if="label" class="flex justify-between items-center flex-nowrap gap-2">
                            {{ label }}
                            <EidIcon
                                v-if="tooltipData"
                                v-tooltip="tooltipData"
                                class="text-eidQualityBlack-50"
                                alt="icon"
                                :icon-src="faQuestionCircle"
                            />
                        </div>
                    </HeadCell>
                </template>
                <CustomPropertyCollateralRow
                    v-for="(collateral, index) in localCollaterals"
                    :key="index"
                    :collateral="collateral"
                    :custom-property-collaterals="customPropertyCollaterals"
                    :updating-insured="updatingEntity"
                    :columns="columns"
                    :rbac-disabled="rbacDisabled"
                    :editing-enabled="editingEnabled"
                    :touched="touched"
                    @input="onCollateralInput($event,index)"
                    @delete="onDeleteCollateral(index)"
                />
            </Table>
        </template>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import createForm from '@evidentid/json-schema/createForm';
    import {
        JsonSchemaAbstractObject,
        JsonSchemaArray,
    } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CustomPropertyCollateral } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';

    import CustomPropertyCollateralRow from './components/CustomPropertyCollateralRow/CustomPropertyCollateralRow.vue';
    import { customPropertyCollateralColumns } from './config';

    export default defineComponent({
        name: 'EntityDetailsCollateralSection',
        components: {
            EidIcon,
            HeadCell,
            CustomPropertyCollateralRow,
            Table,
        },
        props: {
            entityDisplayName: {
                type: String as PropType<string>,
                required: true,
            },
            customPropertyCollaterals: {
                type: Object as PropType<CustomProperty>,
                required: true,
            },
            collaterals: {
                type: Array as PropType<CustomPropertyCollateral[]>,
                default: (): CustomPropertyCollateral[] => [] as CustomPropertyCollateral[],
            },
            updatingEntity: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            editingEnabled: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            touched: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'input', 'validity-change' ],
        data() {
            const fieldsDescription = (this.customPropertyCollaterals.schema as any).items.properties;
            return {
                faQuestionCircle,
                // Copied rather than annotated in place: the column config is shared by every
                // section, and each one's tooltips come from its own property's schema.
                columns: customPropertyCollateralColumns.map((col) => (
                    col.field
                        ? { ...col, tooltipData: { content: fieldsDescription[col.field]?.description || '' } }
                        : col
                )),
                localCollaterals: [] as CustomPropertyCollateral[],
            };
        },
        computed: {
            collateralForm(): ReturnType<typeof createForm> {
                const schema =
                    (this.customPropertyCollaterals.schema as JsonSchemaArray<JsonSchemaAbstractObject>)
                        .items;
                return createForm(schema);
            },
            allCollateralsValid(): boolean {
                if (!this.localCollaterals || this.localCollaterals.length === 0) {
                    return true;
                }
                // The schema form is the whole check: an empty currency cell emits null, which it
                // already rejects for a required field, so a collateral worth nothing is a real
                // value rather than a blank one.
                return this.localCollaterals.every(
                    (collateral) => this.collateralForm.isValid(this.collateralForm.getValue(collateral, true), true),
                );
            },
        },
        watch: {
            collaterals: {
                immediate: true,
                handler(newValue?: boolean) {
                    if (newValue) {
                        this.localCollaterals = [ ...this.collaterals ];
                    }
                },
            },
            allCollateralsValid: {
                immediate: true,
                handler(isValid: boolean): void {
                    this.$emit('validity-change', isValid);
                },
            },
        },
        methods: {
            emitInput(): void {
                this.$emit('input', this.localCollaterals);
            },
            onClickAddCollateral(): void {
                this.localCollaterals = [ ...this.localCollaterals, {} as CustomPropertyCollateral ];
                this.emitInput();
            },
            onCollateralInput(collateral: CustomPropertyCollateral, listIndex: number): void {
                this.localCollaterals = [
                    ...this.localCollaterals.slice(0, listIndex),
                    collateral,
                    ...this.localCollaterals.slice(listIndex + 1),
                ];
                this.emitInput();
            },
            onDeleteCollateral(collateralIndex: number): void {
                this.localCollaterals = [
                    ...this.localCollaterals.slice(0, collateralIndex),
                    ...this.localCollaterals.slice(collateralIndex + 1),
                ];
                this.emitInput();
            },
        },
    });
</script>
